import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchCafeList } from "../api";
import CafeCard from "./CafeCard";
import CafeDropdown, { getCafeIdentifier } from "./CafeDropdown";

const parseCafeResponse = (data) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data;
  }

  if (data.cafes) {
    return data.cafes;
  }

  if (data.data) {
    return data.data;
  }

  if (data.stores) {
    return data.stores;
  }

  return [];
};

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

export default function LocationDropdownTailwind({ onCafeSelect, initialSelectedId = "" }) {
  const [cafes, setCafes] = useState([]);
  const [selectedCafeId, setSelectedCafeId] = useState(initialSelectedId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const loadCafes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchCafeList();
      const cafesData = parseCafeResponse(data);

      setCafes(cafesData);
    } catch (err) {
      setError(err.message || "Unable to load cafes");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load cafes only once on mount
  useEffect(() => {
    loadCafes();
  }, [loadCafes]);

  // Sort cafes by distance when user location becomes available
  useEffect(() => {
    if (userLocation && cafes.length > 0) {
      // Check if cafes are already sorted (have distance property)
      const alreadySorted = cafes.some(cafe => cafe.distance !== undefined);
      if (alreadySorted) {
        return;
      }

      const sortedCafes = cafes.map(cafe => ({
        ...cafe,
        distance: calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          cafe.lat,
          cafe.lng
        )
      })).sort((a, b) => a.distance - b.distance);

      setCafes(sortedCafes);
    }
  }, [userLocation, cafes]);

  useEffect(() => {
    setSelectedCafeId(initialSelectedId);
  }, [initialSelectedId]);

  // Request user's location on component mount
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationError(null);
        },
        (error) => {
          console.warn("Geolocation error:", error);
          setLocationError(error.message);
          // Continue without location - cafes will be shown in original order
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // Cache location for 5 minutes
        }
      );
    } else {
      setLocationError("Geolocation not supported");
    }
  }, []);

  const handleSubmit = () => {
    const selectedCafe = cafes.find((cafe) => getCafeIdentifier(cafe) === selectedCafeId);

    if (selectedCafe) {
      onCafeSelect(selectedCafe);
      return;
    }

    if (selectedCafeId) {
      onCafeSelect({ storeId: selectedCafeId });
    }
  };

  const cardBody = useMemo(() => {
    if (loading) {
      return (
        <div className="text-base text-slate-600">Loading cafes…</div>
      );
    }

    if (error) {
      return (
        <div className="space-y-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            className="w-full h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            onClick={loadCafes}
          >
            Try Again
          </button>
        </div>
      );
    }

    return (
      <>
        <CafeDropdown
          cafes={cafes}
          value={selectedCafeId}
          onChange={setSelectedCafeId}
          placeholder="Select a café"
        />
        <button
          type="button"
          className="w-full h-12 rounded-2xl bg-[#46918A] text-base font-semibold text-white shadow-sm transition hover:bg-[#3a7770] disabled:cursor-not-allowed disabled:bg-slate-300"
          onClick={handleSubmit}
          disabled={!selectedCafeId}
        >
          Continue
        </button>
      </>
    );
  }, [cafes, error, loadCafes, loading, selectedCafeId]);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-[#DBECDF] via-[#DBECDF] to-[#DBECDF] grid place-items-center px-4 py-8">
      <CafeCard
        title="Select Your Café"
        description="Choose a location to continue."
        logoSrc="/GotTeaLogo.png"
        logoAlt="Got Tea logo"
      >
        {cardBody}
      </CafeCard>
    </div>
  );
}
